/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GtoUom } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  dictionaryValueFormatter,
  EDITABLE_CELL_GREEN_CLASS,
  likeEditableCellRenderer,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { EnumDictionary, getRouteInitData, NumberTypes, State } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableQuantityColumn,
  buildNumberColumn,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  ManualGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef } from 'ag-grid-community';
import { isNil } from 'lodash';
import {
  getJeTopId,
  getJeTopQuantityType,
  JeTop,
  jeTopFieldState,
  validateJeTopField,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import { JeDetailsState, jeTopComparator, queryJeEditable } from '../je-details.state';

@Injectable()
export class JeDetailsTopGridDefinition extends ManualGridDefinitionWithModel {
  gtoUomDict: EnumDictionary<GtoUom>;

  constructor(private route: ActivatedRoute,
              private translationService: TranslationService,
              private state: State<JeDetailsState>,
              private commands: JeCommandService) {
    super();

    this.gtoUomDict = getRouteInitData(route)['gtoUomDict'];
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<JeTop>({
      editable: this.state.select(queryJeEditable),
      getId: getJeTopId,
      validate: validateJeTopField,
      getCellDef: jeTopFieldState,
      command: (entity, name, value) => this.commands.topSetField(getJeTopId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      defaultColDef: {
        sortable: false,
      },
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: JeTop }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): ColDef[] {
    return [{
      field: 'code',
      sort: 'asc',
      comparator: gridCollectionComparator(jeTopComparator),
      headerName: this.translationService.get('lbl_code'),
      width: 150,
    }, {
      field: 'value',
      headerName: this.translationService.get('lbl_value'),
      width: 90,
      ...buildEditableQuantityColumn(getJeTopQuantityType, this.model.getCellAccessor('value')),
    }, {
      field: 'gtoUom',
      headerName: this.translationService.get('lbl_uom_abbreviation'),
      width: 70,
      valueFormatter: dictionaryValueFormatter(this.gtoUomDict),
    }, {
      field: 'count',
      headerName: this.translationService.get('lbl_count'),
      width: 65,
      ...buildNumberColumn(NumberTypes.Quantity),
      cellRenderer: likeEditableCellRenderer,
      cellClassRules: {
        [EDITABLE_CELL_GREEN_CLASS]: ({ data }: { data: JeTop }) => data.isFromGto,
      },
    }];
  }
}
